import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Search, Globe, Loader2, Plus, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { MealTimeSelector } from '../components/MealTimeSelector';

const API_URL = '';

export function FoodSearchModal({
  isOpen,
  onClose,
  onAddFood,
  onOpenManualEntry,
  defaultMealTime = 'breakfast',
  mealTimes,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [servingSize, setServingSize] = useState('');
  const [selectedMealTime, setSelectedMealTime] = useState(defaultMealTime);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      const response = await fetch(
        `${API_URL}/api/barcode/search?query=${encodeURIComponent(searchQuery)}&page_size=20`
      );
      
      if (response.status === 503) {
        toast.error('Food database is temporarily unavailable. Please try again later.');
        return;
      }
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      
      if (data.products) {
        const results = data.products
          .filter(p => p.name && p.name !== 'Unknown')
          .map(p => ({
            id: p.barcode || p.name,
            name: p.name,
            brand: p.brand,
            image: p.image,
            calories: p.calories || 0,
            protein: p.protein || 0,
            carbs: p.carbs || 0,
            fat: p.fat || 0,
          }));
        setSearchResults(results);
        
        if (results.length === 0) {
          toast.info('No products found. Try a different search term.');
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Food database unavailable. Please try again later.');
    } finally {
      setIsSearching(false);
    }
  };

  const calculateNutrition = (product, grams) => {
    const portion = parseInt(grams) || 0;
    const multiplier = portion / 100;
    return {
      ...product,
      calories: Math.round(product.calories * multiplier),
      protein: Math.round(product.protein * multiplier * 10) / 10,
      carbs: Math.round(product.carbs * multiplier * 10) / 10,
      fat: Math.round(product.fat * multiplier * 10) / 10,
      grams: portion,
    };
  };

  const handleAddFood = (product) => {
    const adjusted = calculateNutrition(product, servingSize);
    onAddFood({ ...adjusted, mealTime: selectedMealTime });
    onClose();
    setSearchQuery('');
    setSearchResults([]);
    setServingSize('');
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setServingSize('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-400" />
            Food Database
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 overflow-hidden">
          {/* Search Input */}
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Search for food..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="bg-blue-600 hover:bg-blue-500"
            >
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Manual Entry Button */}
          {onOpenManualEntry && (
            <Button
              variant="outline"
              onClick={() => {
                handleClose();
                onOpenManualEntry();
              }}
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Add Manually
            </Button>
          )}

          {/* Serving Size */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-400 whitespace-nowrap">Serving:</label>
            <Input
              type="number"
              value={servingSize}
              onChange={(e) => setServingSize(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white w-24"
              placeholder="g"
              min="1"
            />
            <span className="text-sm text-slate-400">g</span>
          </div>

          {/* Meal Time Selection */}
          <div className="space-y-1">
            <label className="text-sm text-slate-400">Add to:</label>
            <MealTimeSelector 
              selectedMealTime={selectedMealTime} 
              onMealTimeChange={setSelectedMealTime}
              compact
              mealTimes={mealTimes}
            />
          </div>

          {/* Search Results */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0 max-h-[50vh]">
            {isSearching && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
              </div>
            )}
            
            {!isSearching && searchResults.length === 0 && searchQuery && (
              <p className="text-center text-slate-500 py-8">
                No results found. Try different search terms or add manually.
              </p>
            )}
            
            {!isSearching && searchResults.map((product) => {
              const adjusted = calculateNutrition(product, servingSize);
              return (
                <div
                  key={product.id}
                  className="p-3 bg-slate-800 rounded-xl border border-slate-700"
                >
                  <div className="flex items-start gap-3">
                    {product.image && (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium text-sm truncate">
                        {product.name}
                      </h4>
                      {product.brand && (
                        <p className="text-slate-500 text-xs truncate">{product.brand}</p>
                      )}
                      <div className="flex gap-2 mt-1 text-xs">
                        <span className="text-orange-400">{adjusted.calories} kcal</span>
                        <span className="text-red-400">P:{adjusted.protein}g</span>
                        <span className="text-yellow-400">C:{adjusted.carbs}g</span>
                        <span className="text-blue-400">F:{adjusted.fat}g</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleAddFood(product)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-500"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FoodSearchModal;
